import React, { useState, useEffect } from 'react';
import subUserService from '../../services/SubUserService';
import { 
  Users, UserCheck, UserX, Plus, Search, RefreshCw, Upload, Download, 
  Edit, Eye, Trash2, Key, Lock, Shield, CheckCircle2, X, ArrowLeft, Save
} from 'lucide-react';

const StaffUserManagement = ({ showNotification }) => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Views: 'list' | 'create' | 'edit' | 'view' | 'resetPassword'
  const [currentView, setCurrentView] = useState('list');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Dashboard Stats
  const [stats, setStats] = useState({ total: 0, active: 0, disabled: 0, locked: 0, passwordReset: 0, twoFactor: 0 });

  const initialFormState = {
    firstName: '', middleName: '', lastName: '', gender: 'Male', dateOfBirth: '', mobileNumber: '', alternateMobile: '',
    email: '', employeeId: '', staffCode: '', department: 'Teaching', designation: 'Teacher', joiningDate: '', employmentType: 'FULL_TIME',
    username: '', password: '', role: 'TEACHER', status: 'ACTIVE', forcePasswordChange: false, twoFactorEnabled: false,
    modulePermissions: ['dashboard']
  };

  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [resetPwData, setResetPwData] = useState({ newPassword: '', confirmPassword: '' });
  
  const modulesList = ['dashboard', 'students', 'teachers', 'attendance', 'fees', 'examination', 'library', 'hostel', 'transport', 'reports', 'settings'];
  const roleOptions = ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'PRINCIPAL', 'VICE_PRINCIPAL', 'HOD', 'TEACHER', 'ACCOUNTANT', 'LIBRARIAN', 'TRANSPORT_MANAGER', 'HOSTEL_WARDEN', 'EXAM_OFFICER', 'RECEPTIONIST', 'COUNSELOR', 'LAB_ASSISTANT', 'HR', 'STAFF', 'CUSTOM'];

  useEffect(() => { loadStaff(); }, []);

  const loadStaff = async () => {
    setLoading(true);
    try {
      const res = await subUserService.getAllStaff();
      if (res && res.success && res.users) {
        setStaff(res.users);
        calculateStats(res.users);
      }
    } catch (err) {
      console.error('Failed to load staff:', err);
      if (showNotification) showNotification('error', 'Error', 'Failed to load staff users.');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (users) => {
    setStats({
      total: users.length,
      active: users.filter(u => u.status === 'ACTIVE').length,
      disabled: users.filter(u => u.status === 'INACTIVE' || u.status === 'SUSPENDED').length,
      locked: users.filter(u => u.locked).length,
      passwordReset: users.filter(u => u.passwordResetRequired || u.forcePasswordChange).length,
      twoFactor: users.filter(u => u.twoFactorEnabled).length,
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const togglePermission = (moduleName) => {
    setFormData(prev => {
      const perms = new Set(prev.modulePermissions);
      perms.has(moduleName) ? perms.delete(moduleName) : perms.add(moduleName);
      return { ...prev, modulePermissions: Array.from(perms) };
    });
  };

  // ── CREATE ──
  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await subUserService.createStaff(formData);
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Staff Created', 'Staff account created successfully!');
        setCurrentView('list');
        setFormData(initialFormState);
        loadStaff();
      } else throw new Error(res.error || 'Creation failed');
    } catch (err) {
      if (showNotification) showNotification('error', 'Error', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── UPDATE ──
  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await subUserService.updateStaff(selectedUser.id, formData);
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Updated', 'Staff profile updated successfully!');
        setCurrentView('list');
        setSelectedUser(null);
        loadStaff();
      } else throw new Error(res.error || 'Update failed');
    } catch (err) {
      if (showNotification) showNotification('error', 'Error', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── DELETE ──
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this staff user? This action cannot be undone.')) return;
    try {
      const res = await subUserService.deleteStaff(id);
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Deleted', 'Staff user deleted.');
        loadStaff();
      } else throw new Error(res.error);
    } catch (err) {
      if (showNotification) showNotification('error', 'Error', err.message);
    }
  };

  // ── RESET PASSWORD ──
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPwData.newPassword !== resetPwData.confirmPassword) {
      if (showNotification) showNotification('error', 'Mismatch', 'Passwords do not match');
      return;
    }
    if (resetPwData.newPassword.length < 6) {
      if (showNotification) showNotification('error', 'Too Short', 'Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await subUserService.resetStaffPassword(selectedUser.id, resetPwData.newPassword);
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Password Reset', 'Password has been reset successfully.');
        setCurrentView('list');
        setResetPwData({ newPassword: '', confirmPassword: '' });
        setSelectedUser(null);
      } else throw new Error(res.error || 'Reset failed');
    } catch (err) {
      if (showNotification) showNotification('error', 'Error', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── STATUS TOGGLE ──
  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      const res = await subUserService.updateStaffStatus(user.id, newStatus);
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Status Changed', `User is now ${newStatus}`);
        loadStaff();
      }
    } catch (err) {
      if (showNotification) showNotification('error', 'Error', err.message);
    }
  };

  // ── OPEN EDIT ──
  const openEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName || '', middleName: user.middleName || '', lastName: user.lastName || '',
      gender: user.gender || 'Male', dateOfBirth: user.dateOfBirth || '', mobileNumber: user.mobileNumber || '',
      alternateMobile: user.alternateMobile || '', email: user.email || '', employeeId: user.employeeId || '',
      staffCode: user.staffCode || '', department: user.department || '', designation: user.designation || '',
      joiningDate: user.joiningDate || '', employmentType: user.employmentType || 'FULL_TIME',
      username: user.username || '', password: '', role: user.role || 'TEACHER', status: user.status || 'ACTIVE',
      forcePasswordChange: user.forcePasswordChange || false, twoFactorEnabled: user.twoFactorEnabled || false,
      modulePermissions: user.modulePermissions ? [...user.modulePermissions] : ['dashboard']
    });
    setCurrentView('edit');
  };

  // ── OPEN VIEW ──
  const openView = (user) => {
    setSelectedUser(user);
    setCurrentView('view');
  };

  // ── OPEN RESET PASSWORD ──
  const openResetPassword = (user) => {
    setSelectedUser(user);
    setResetPwData({ newPassword: '', confirmPassword: '' });
    setCurrentView('resetPassword');
  };

  const goBack = () => {
    setCurrentView('list');
    setSelectedUser(null);
    setFormData(initialFormState);
  };

  const filteredStaff = staff.filter(s => {
    const q = searchQuery.toLowerCase();
    return !q || (s.firstName || '').toLowerCase().includes(q) || (s.lastName || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) || (s.employeeId || '').toLowerCase().includes(q) ||
      (s.username || '').toLowerCase().includes(q) || (s.role || '').toLowerCase().includes(q) ||
      (s.department || '').toLowerCase().includes(q);
  });

  // ════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════
  return (
    <div style={{ background: '#f8fafc', minHeight: '80vh', padding: '24px', borderRadius: '12px' }}>
      
      {/* ── HEADER ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {currentView !== 'list' && (
            <button onClick={goBack} style={{ padding: '8px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', display: 'flex' }}>
              <ArrowLeft size={18} />
            </button>
          )}
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' }}>
              {currentView === 'create' ? 'Create Staff User' : currentView === 'edit' ? 'Edit Staff User' : currentView === 'view' ? 'Staff Profile' : currentView === 'resetPassword' ? 'Reset Password' : 'Staff User Management'}
            </h2>
            <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>
              {currentView === 'list' ? 'Manage employee accounts, permissions, and security access.' : currentView === 'view' ? `Viewing profile for ${selectedUser?.firstName || selectedUser?.username}` : currentView === 'resetPassword' ? `Set a new password for ${selectedUser?.firstName || selectedUser?.username}` : 'Fill in staff details below.'}
            </p>
          </div>
        </div>
        {currentView === 'list' && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <HeaderBtn icon={<Upload size={16} />} label="Import" onClick={() => {}} />
            <HeaderBtn icon={<Download size={16} />} label="Export" onClick={() => {}} />
            <HeaderBtn icon={<RefreshCw size={16} />} label="Refresh" onClick={loadStaff} />
            <button onClick={() => { setFormData(initialFormState); setCurrentView('create'); }} style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
              <Plus size={16} /> Create Staff
            </button>
          </div>
        )}
      </div>

      {/* ── DASHBOARD CARDS ── */}
      {currentView === 'list' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          <StatCard title="Total Staff" value={stats.total} icon={<Users size={22} color="#3b82f6" />} bg="#eff6ff" />
          <StatCard title="Active" value={stats.active} icon={<UserCheck size={22} color="#16a34a" />} bg="#dcfce7" />
          <StatCard title="Disabled" value={stats.disabled} icon={<UserX size={22} color="#dc2626" />} bg="#fee2e2" />
          <StatCard title="Locked" value={stats.locked} icon={<Lock size={22} color="#ea580c" />} bg="#ffedd5" />
          <StatCard title="Pending Reset" value={stats.passwordReset} icon={<Key size={22} color="#ca8a04" />} bg="#fef9c3" />
          <StatCard title="2FA Enabled" value={stats.twoFactor} icon={<Shield size={22} color="#7c3aed" />} bg="#f3e8ff" />
        </div>
      )}

      {/* ════════════════ LIST VIEW ════════════════ */}
      {currentView === 'list' && (
        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {/* Search */}
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ position: 'relative', maxWidth: '360px' }}>
              <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input type="text" placeholder="Search by name, email, ID, role..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                     style={{ width: '100%', padding: '10px 10px 10px 36px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none', fontSize: '14px' }} />
            </div>
          </div>
          
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
              <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTop: '3px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }}></div>
              Loading staff data...
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div style={{ padding: '80px 20px', textAlign: 'center' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <Users size={36} color="#94a3b8" />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>No Staff Members Found</h3>
              <p style={{ color: '#64748b', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>Create your first administrative account to delegate responsibilities to your staff.</p>
              <button onClick={() => { setFormData(initialFormState); setCurrentView('create'); }} style={{ padding: '10px 28px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>
                Create First Staff User
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                    <th style={thStyle}>Name & ID</th>
                    <th style={thStyle}>Contact</th>
                    <th style={thStyle}>Role / Dept</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaff.map(s => (
                    <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#fff', fontSize: '14px', flexShrink: 0 }}>
                            {(s.firstName || s.username || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', color: '#1e293b' }}>{s.firstName || ''} {s.lastName || ''}{!s.firstName && !s.lastName ? s.username : ''}</div>
                            <div style={{ color: '#94a3b8', fontSize: '12px' }}>{s.employeeId || s.username}</div>
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ color: '#1e293b', fontSize: '13px' }}>{s.email}</div>
                        <div style={{ color: '#94a3b8', fontSize: '12px' }}>{s.mobileNumber || '—'}</div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '16px', fontSize: '11px', fontWeight: '600' }}>
                          {(s.role || '').replace(/_/g, ' ')}
                        </span>
                        <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px' }}>{s.department || '—'}</div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ 
                          padding: '3px 10px', borderRadius: '16px', fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                          background: s.status === 'ACTIVE' ? '#dcfce7' : s.status === 'BLOCKED' || s.status === 'SUSPENDED' ? '#ffedd5' : '#fee2e2', 
                          color: s.status === 'ACTIVE' ? '#166534' : s.status === 'BLOCKED' || s.status === 'SUSPENDED' ? '#9a3412' : '#991b1b' 
                        }} onClick={() => handleToggleStatus(s)} title="Click to toggle status">
                          {s.status}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <ActionBtn icon={<Eye size={15} />} color="#64748b" title="View Details" onClick={() => openView(s)} />
                          <ActionBtn icon={<Edit size={15} />} color="#3b82f6" title="Edit" onClick={() => openEdit(s)} />
                          <ActionBtn icon={<Key size={15} />} color="#ca8a04" title="Reset Password" onClick={() => openResetPassword(s)} />
                          <ActionBtn icon={<Trash2 size={15} />} color="#ef4444" title="Delete" onClick={() => handleDelete(s.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ════════════════ CREATE / EDIT FORM ════════════════ */}
      {(currentView === 'create' || currentView === 'edit') && (
        <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <form onSubmit={currentView === 'create' ? handleCreate : handleUpdate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
              
              {/* LEFT */}
              <div>
                <SectionTitle title="Personal Information" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <Input label="First Name *" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
                  <Input label="Last Name *" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
                  <Select label="Gender" name="gender" value={formData.gender} onChange={handleInputChange} options={['Male', 'Female', 'Other']} />
                  <Input label="Date of Birth" type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} />
                  <Input label="Email *" type="email" name="email" value={formData.email} onChange={handleInputChange} required />
                  <Input label="Mobile Number" name="mobileNumber" value={formData.mobileNumber} onChange={handleInputChange} />
                </div>

                <SectionTitle title="Employment Details" style={{ marginTop: '32px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <Input label="Employee ID" name="employeeId" value={formData.employeeId} onChange={handleInputChange} placeholder="Auto-generated if blank" disabled={currentView === 'edit'} />
                  <Input label="Department" name="department" value={formData.department} onChange={handleInputChange} />
                  <Input label="Designation" name="designation" value={formData.designation} onChange={handleInputChange} />
                  <Select label="Employment Type" name="employmentType" value={formData.employmentType} onChange={handleInputChange} options={['FULL_TIME', 'PART_TIME', 'CONTRACT', 'GUEST', 'INTERN']} />
                  <Input label="Joining Date" type="date" name="joiningDate" value={formData.joiningDate} onChange={handleInputChange} />
                  <Select label="System Role *" name="role" value={formData.role} onChange={handleInputChange} options={roleOptions} />
                </div>
              </div>

              {/* RIGHT */}
              <div>
                <SectionTitle title="Authentication & Security" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <Input label="Username *" name="username" value={formData.username} onChange={handleInputChange} required disabled={currentView === 'edit'} />
                  <Select label="Account Status" name="status" value={formData.status} onChange={handleInputChange} options={['ACTIVE', 'INACTIVE', 'SUSPENDED', 'BLOCKED']} />
                  {currentView === 'create' && (
                    <Input label="Password *" type="password" name="password" value={formData.password} onChange={handleInputChange} required />
                  )}
                </div>
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Checkbox label="Force Password Change on First Login" name="forcePasswordChange" checked={formData.forcePasswordChange} onChange={handleInputChange} />
                  <Checkbox label="Enable Two-Factor Authentication" name="twoFactorEnabled" checked={formData.twoFactorEnabled} onChange={handleInputChange} />
                </div>

                <SectionTitle title="Module Permissions" style={{ marginTop: '32px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {modulesList.map(mod => (
                    <div key={mod} onClick={() => togglePermission(mod)} style={{ 
                      padding: '10px 12px', border: formData.modulePermissions.includes(mod) ? '2px solid #2563eb' : '1px solid #e2e8f0', 
                      borderRadius: '8px', cursor: 'pointer', background: formData.modulePermissions.includes(mod) ? '#eff6ff' : '#fff',
                      display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.15s'
                    }}>
                      {formData.modulePermissions.includes(mod) ? <CheckCircle2 size={16} color="#2563eb" /> : <div style={{width: 16, height: 16, border: '1.5px solid #cbd5e1', borderRadius: '50%'}}></div>}
                      <span style={{ fontSize: '13px', fontWeight: '500', color: formData.modulePermissions.includes(mod) ? '#1e3a8a' : '#475569', textTransform: 'capitalize' }}>
                        {mod}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* BUTTONS */}
            <div style={{ marginTop: '36px', display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
              <button type="button" onClick={goBack} style={cancelBtnStyle}>Cancel</button>
              <button type="submit" disabled={submitting} style={{ ...primaryBtnStyle, opacity: submitting ? 0.7 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}>
                <Save size={16} /> {submitting ? 'Saving...' : currentView === 'create' ? 'Create Staff User' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ════════════════ VIEW DETAILS ════════════════ */}
      {currentView === 'view' && selectedUser && (
        <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          {/* Profile Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#fff', fontSize: '28px', flexShrink: 0 }}>
              {(selectedUser.firstName || selectedUser.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' }}>
                {selectedUser.firstName || ''} {selectedUser.middleName || ''} {selectedUser.lastName || ''}{!selectedUser.firstName ? selectedUser.username : ''}
              </h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ padding: '3px 10px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '16px', fontSize: '12px', fontWeight: '600' }}>{(selectedUser.role || '').replace(/_/g, ' ')}</span>
                <span style={{ padding: '3px 10px', background: selectedUser.status === 'ACTIVE' ? '#dcfce7' : '#fee2e2', color: selectedUser.status === 'ACTIVE' ? '#166534' : '#991b1b', borderRadius: '16px', fontSize: '12px', fontWeight: '600' }}>{selectedUser.status}</span>
                {selectedUser.employeeId && <span style={{ color: '#64748b', fontSize: '13px' }}>ID: {selectedUser.employeeId}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => openEdit(selectedUser)} style={{ ...primaryBtnStyle, fontSize: '13px', padding: '8px 16px' }}><Edit size={14} /> Edit</button>
              <button onClick={() => openResetPassword(selectedUser)} style={{ ...cancelBtnStyle, fontSize: '13px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}><Key size={14} /> Reset Password</button>
            </div>
          </div>

          {/* Profile Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
            <div>
              <SectionTitle title="Personal Information" />
              <div style={{ display: 'grid', gap: '12px' }}>
                <InfoRow label="First Name" value={selectedUser.firstName} />
                <InfoRow label="Middle Name" value={selectedUser.middleName} />
                <InfoRow label="Last Name" value={selectedUser.lastName} />
                <InfoRow label="Gender" value={selectedUser.gender} />
                <InfoRow label="Date of Birth" value={selectedUser.dateOfBirth} />
                <InfoRow label="Email" value={selectedUser.email} />
                <InfoRow label="Mobile" value={selectedUser.mobileNumber} />
                <InfoRow label="Alternate Mobile" value={selectedUser.alternateMobile} />
              </div>
            </div>
            <div>
              <SectionTitle title="Employment & Security" />
              <div style={{ display: 'grid', gap: '12px' }}>
                <InfoRow label="Employee ID" value={selectedUser.employeeId} />
                <InfoRow label="Department" value={selectedUser.department} />
                <InfoRow label="Designation" value={selectedUser.designation} />
                <InfoRow label="Employment Type" value={selectedUser.employmentType} />
                <InfoRow label="Joining Date" value={selectedUser.joiningDate} />
                <InfoRow label="Username" value={selectedUser.username} />
                <InfoRow label="2FA Enabled" value={selectedUser.twoFactorEnabled ? 'Yes' : 'No'} />
                <InfoRow label="Force Password Change" value={selectedUser.forcePasswordChange ? 'Yes' : 'No'} />
                <InfoRow label="Last Login" value={selectedUser.lastLogin || '—'} />
              </div>
            </div>
          </div>

          {/* Permissions */}
          {selectedUser.modulePermissions && selectedUser.modulePermissions.length > 0 && (
            <div style={{ marginTop: '32px' }}>
              <SectionTitle title="Module Permissions" />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {selectedUser.modulePermissions.map(p => (
                  <span key={p} style={{ padding: '6px 14px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '20px', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize' }}>
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════════════ RESET PASSWORD ════════════════ */}
      {currentView === 'resetPassword' && selectedUser && (
        <div style={{ maxWidth: '480px', margin: '0 auto' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fef9c3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <Key size={24} color="#ca8a04" />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' }}>Reset Password</h3>
              <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>Set a new password for <strong>{selectedUser.firstName || selectedUser.username}</strong></p>
            </div>
            <form onSubmit={handleResetPassword}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <Input label="New Password *" type="password" name="newPassword" value={resetPwData.newPassword} onChange={e => setResetPwData(prev => ({ ...prev, newPassword: e.target.value }))} required />
                <Input label="Confirm Password *" type="password" name="confirmPassword" value={resetPwData.confirmPassword} onChange={e => setResetPwData(prev => ({ ...prev, confirmPassword: e.target.value }))} required />
              </div>
              {resetPwData.newPassword && resetPwData.confirmPassword && resetPwData.newPassword !== resetPwData.confirmPassword && (
                <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '8px' }}>⚠ Passwords do not match</p>
              )}
              <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
                <button type="button" onClick={goBack} style={{ ...cancelBtnStyle, flex: 1 }}>Cancel</button>
                <button type="submit" disabled={submitting} style={{ ...primaryBtnStyle, flex: 1, justifyContent: 'center', opacity: submitting ? 0.7 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}>
                  {submitting ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════
//  STYLES
// ═══════════════════════════════
const thStyle = { padding: '14px 20px', fontWeight: '600', color: '#475569', fontSize: '13px', whiteSpace: 'nowrap' };
const tdStyle = { padding: '14px 20px' };
const cancelBtnStyle = { padding: '10px 24px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' };
const primaryBtnStyle = { padding: '10px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' };

// ═══════════════════════════════
//  HELPER COMPONENTS
// ═══════════════════════════════
const HeaderBtn = ({ icon, label, onClick }) => (
  <button onClick={onClick} style={{ padding: '8px 14px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}>
    {icon} {label}
  </button>
);

const ActionBtn = ({ icon, color, title, onClick }) => (
  <button onClick={onClick} style={{ padding: '6px', background: 'transparent', border: '1px solid transparent', borderRadius: '6px', color, cursor: 'pointer', display: 'flex', transition: 'background 0.15s' }} title={title}
    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
    {icon}
  </button>
);

const StatCard = ({ title, value, icon, bg }) => (
  <div style={{ background: '#fff', borderRadius: '12px', padding: '18px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '14px' }}>
    <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </div>
    <div>
      <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '500', marginBottom: '2px' }}>{title}</div>
      <div style={{ color: '#0f172a', fontSize: '22px', fontWeight: '700' }}>{value}</div>
    </div>
  </div>
);

const SectionTitle = ({ title, style }) => (
  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#334155', borderBottom: '2px solid #f1f5f9', paddingBottom: '8px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px', ...style }}>
    {title}
  </h4>
);

const Input = ({ label, required, ...props }) => (
  <div>
    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' }}>{label}</label>
    <input style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', outline: 'none', fontSize: '14px', boxSizing: 'border-box' }} required={required} {...props} />
  </div>
);

const Select = ({ label, options, ...props }) => (
  <div>
    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' }}>{label}</label>
    <select style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', outline: 'none', fontSize: '14px', background: '#fff', boxSizing: 'border-box' }} {...props}>
      {options.map(opt => <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>)}
    </select>
  </div>
);

const Checkbox = ({ label, ...props }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#334155', cursor: 'pointer' }}>
    <input type="checkbox" style={{ width: '16px', height: '16px', accentColor: '#2563eb' }} {...props} /> {label}
  </label>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
    <span style={{ color: '#64748b', fontSize: '13px' }}>{label}</span>
    <span style={{ color: '#1e293b', fontSize: '13px', fontWeight: '500' }}>{value || '—'}</span>
  </div>
);

export default StaffUserManagement;
